import type {
  InventorySnapshotVolume,
  InventoryResource,
  RestoreAccount,
  Snapshot,
} from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';

import type { VolumeConfigState } from './restoreEc2InstanceWizard';
import { ConvertToAMIVolumeConfiguration } from './steps/convertAMIVolumeConfiguration';
import { ImageConfiguration } from './steps/imageConfiguration';
import { SelectTargetAccountStep } from './steps/selectTargetAccount';

import type { Encryption } from '../encryption/encryptionComponent';
import type { Step } from '../wizard/Step';
import { Wizard } from '../wizard/Wizard';

export interface ConvertToAMIState {
  imageId?: string;
  useCustomAMI?: boolean;
  name?: string;
  description?: string;
  resourceId: string;
  resourceName: string;
  snapshot: Snapshot;
  supportedCloudProviders: Provider[];
  restoreAccount?: RestoreAccount;
  sharedEncryption?: Encryption;
  regionName?: string;
  tags: Record<string, string>;
  keepOriginalTags: boolean;
  volumesConfigState?: VolumeConfigState[];
  volumes: InventorySnapshotVolume[];
  resource?: InventoryResource;
}

const convertToAMIFlow: Step<ConvertToAMIState> = {
  id: 'select_target_account_step',
  Component: SelectTargetAccountStep,
  next: [
    {
      id: 'image_configuration',
      Component: ImageConfiguration,
      next: [
        {
          id: 'convert_to_ami_volume_configuration',
          Component: ConvertToAMIVolumeConfiguration,
        },
      ],
    },
  ],
};

export const ConvertToAMIWizard = ({
  resource,
  resourceId,
  resourceName,
  snapshot,
  volumes,
  onClose,
  onBack,
}: {
  resource?: InventoryResource;
  resourceId: string;
  resourceName: string;
  snapshot: Snapshot;
  volumes: InventorySnapshotVolume[];
  onClose: () => void;
  onBack: () => void;
}) => {
  const tags = snapshot.resource?.tags || {};
  const volumesSettings: VolumeConfigState[] = volumes.map((vol) => ({
    ...vol,
    isEncrypted: true,
    volumeEncryptionKeyId: '',
    encryptionKeyId: undefined,
    keepOriginalSettings: true,
    keepOriginalTags: true,
    tags: vol.tags || {},
  }));

  return (
    <Wizard<ConvertToAMIState>
      onAbort={(origin) => (origin === 'back' ? onBack() : onClose())}
      onFinish={async () => true}
      flow={[convertToAMIFlow]}
      initialState={{
        snapshot,
        resource,
        resourceId,
        resourceName,
        supportedCloudProviders: [Provider.Aws],
        volumes,
        sharedEncryption: {
          isEncrypted: true,
          encryptionKeyId: undefined,
          arn: undefined,
          mode: 'select',
        },
        regionName:
          snapshot.resource?.properties?.awsEc2?.volumes?.[0]?.region ||
          'us-east-1',
        tags,
        keepOriginalTags: true,
        volumesConfigState: volumesSettings,
        useCustomAMI: false,
      }}
      title={`Convert to AMI`}
      stepperLabels={[
        'Restore account',
        'Image configuration',
        'Volume configuration',
      ]}
    />
  );
};
